import { useEffect, useRef } from "react";
import axios from "axios";
import { devError } from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DETAIL_FILTERS = ["evidence", "pending", "owner"];

function initialDirectoryFilters(params) {
  return {
    category: params.get("category"),
    county: params.get("county"),
    details: params.get("details"),
    domain: params.get("domain"),
    grade: params.get("grade"),
    proof: params.get("proof"),
    query: params.get("q"),
    radius: params.get("radius"),
    zip: params.get("zip"),
    zone: params.get("zone"),
  };
}

function applyInitialFilters(filters, actions) {
  const assignments = [
    [filters.category, actions.setActiveCategory],
    [filters.domain, actions.setActiveDomain],
    [filters.query, actions.setSearchQuery],
    [filters.grade, actions.setActiveGrade],
  ];
  assignments.forEach(([value, setter]) => { if (value) setter(value); });
  if (filters.zone) actions.setActiveZone(filters.zone);
  else if (filters.county) actions.setActiveCounty(filters.county);
  if (DETAIL_FILTERS.includes(filters.details)) actions.setDetailsFilter(filters.details);
}

async function nearbyFromZip(zip, radius) {
  const distanceModule = await import("@/utils/geo");
  const constantsModule = await import("@/utils/constants");
  const response = await axios.get(`${API}/geocode/zip/${zip}`);
  const data = response.data;
  const cities = Object.entries(constantsModule.ZONE_COORDS)
    .filter(([name, coordinates]) => name !== "default" && coordinates)
    .map(([name, coordinates]) => ({
      name,
      distance: distanceModule.getDistanceMiles(data.lat, data.lng, coordinates[0], coordinates[1]),
    }))
    .filter((city) => city.distance <= radius)
    .sort((left, right) => left.distance - right.distance)
    .map((city) => city.name);
  return { zip, radius, cityLabel: `${data.city}, ${data.state}`, lat: data.lat, lng: data.lng, cities };
}

function applyNearbyState(nearby, actions) {
  actions.setNearbyZip(nearby);
  actions.setUserLocation({ lat: Number(nearby.lat.toFixed(3)), lng: Number(nearby.lng.toFixed(3)) });
  actions.setLocationSource("zip");
  actions.setGeoStatus("granted");
  actions.setSortBy("nearby");
}

export function useDirectoryDeepLinks({ actions, nearbyZip, searchParams, setSearchParams }) {
  const initialParams = useRef(new URLSearchParams(searchParams)).current;

  useEffect(() => {
    let active = true;
    const filters = initialDirectoryFilters(initialParams);
    applyInitialFilters(filters, actions);
    const zipIsValid = filters.zip && /^\d{5}$/.test(filters.zip);
    if (zipIsValid) {
      const radius = Math.max(5, Math.min(200, parseInt(filters.radius, 10) || 25));
      nearbyFromZip(filters.zip, radius)
        .then((nearby) => { if (active) applyNearbyState(nearby, actions); })
        .catch((error) => devError("Unable to apply nearby ZIP filter from URL:", error));
    }
    if (Object.values(filters).some(Boolean)) {
      window.setTimeout(() => document.getElementById("listings-section")?.scrollIntoView({ behavior: "smooth" }), 400);
    }
    return () => { active = false; };
  }, [actions, initialParams]);

  useEffect(() => {
    if (!searchParams.get("proof")) return;
    const details = searchParams.get("details");
    actions.setActiveCategory("all");
    actions.setActiveZone(null);
    actions.setActiveCounty(null);
    actions.setActiveDomain(null);
    actions.setNearbyZip(null);
    actions.setUserLocation(null);
    actions.setLocationSource(null);
    actions.setGeoStatus("idle");
    actions.setSortBy("default");
    actions.setActiveGrade(searchParams.get("grade") || null);
    actions.setDetailsFilter(DETAIL_FILTERS.includes(details) ? details : "all");
    actions.setSearchQuery(searchParams.get("q") || "");
    window.setTimeout(() => document.getElementById("listings-section")?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [actions, searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (nearbyZip) {
      params.set("zip", nearbyZip.zip);
      params.set("radius", String(nearbyZip.radius));
    } else {
      params.delete("zip");
      params.delete("radius");
    }
    if (params.toString() !== searchParams.toString()) setSearchParams(params, { replace: true });
  }, [nearbyZip, searchParams, setSearchParams]);
}